import { Query } from '@apollo/client/react/components';
import { KeySet } from '@tableau/data-structures';
import React from 'react';
import { TableLineageCountsFilteredByColumnQuery, TableLineageCountsFilteredByColumnQueryVariables, TableLineageCountsQuery, TableLineageCountsQueryVariables, TableLineageCountsFilteredByColumnDocument, TableLineageCountsDocument } from '../../../../../gen-src/queries';
import { LineageCountsParentProps } from '../lineage/LineageCountsCacher';
import { renderFilteredLineageCounts, renderUnfilteredLineageCounts } from '../lineage/LineageCountsFunctions';

export type TableLineageCounts = {
  readonly filteredColumnName: string | null;
  readonly upstreamDatabases: number;
  readonly upstreamTables: number;
  readonly upstreamFlows: number;
  readonly upstreamDatasources: number;
  readonly columns: number;
  readonly downstreamDashboards: number;
  readonly downstreamDatabases: number;
  readonly downstreamTables: number;
  readonly downstreamVirtualConnections: number;
  readonly downstreamVirtualConnectionTables: number;
  readonly downstreamFlows: number;
  readonly downstreamDatasources: number;
  readonly downstreamLenses: number;
  readonly downstreamWorkbooks: number;
  readonly downstreamSheets: number;
  readonly downstreamMetrics: number;
  readonly downstreamOwners: number;
};

function createUnfilteredCounts(data: TableLineageCountsQuery): TableLineageCounts | null {
  const table = data.tables && data.tables[0];
  if (table
    && table.unfilteredColumnsConnection
    && table.upstreamDatabasesConnection
    && table.upstreamTablesConnection
    && table.upstreamFlowsConnection
    && table.upstreamDatasourcesConnection
    && table.downstreamDashboardsConnection
    && table.downstreamDatabasesConnection
    && table.downstreamTablesConnection
    && table.downstreamFlowsConnection
    && table.downstreamDatasourcesConnection
    && table.downstreamWorkbooksConnection
    && table.downstreamSheetsConnection
    && table.downstreamMetricsConnection
    && table.downstreamOwnersConnection
  ) {
    return {
      filteredColumnName: null,
      columns: table.unfilteredColumnsConnection.totalCount,
      upstreamDatabases: table.upstreamDatabasesConnection.totalCount,
      upstreamTables: table.upstreamTablesConnection.totalCount,
      upstreamFlows: table.upstreamFlowsConnection.totalCount,
      upstreamDatasources: table.upstreamDatasourcesConnection.totalCount,
      downstreamDashboards: table.downstreamDashboardsConnection.totalCount,
      downstreamDatabases: table.downstreamDatabasesConnection.totalCount,
      downstreamTables: table.downstreamTablesConnection.totalCount,
      downstreamVirtualConnections: table.downstreamVirtualConnectionsConnection?.totalCount || 0,
      downstreamVirtualConnectionTables: table.downstreamVirtualConnectionTablesConnection?.totalCount || 0,
      downstreamFlows: table.downstreamFlowsConnection.totalCount,
      downstreamDatasources: table.downstreamDatasourcesConnection.totalCount,
      downstreamLenses: table.downstreamLensesConnection?.totalCount || 0,
      downstreamWorkbooks: table.downstreamWorkbooksConnection.totalCount,
      downstreamSheets: table.downstreamSheetsConnection.totalCount,
      downstreamMetrics: table.downstreamMetricsConnection.totalCount,
      downstreamOwners: table.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

function createFilteredCounts(data: TableLineageCountsFilteredByColumnQuery): TableLineageCounts | null {
  const table = data.tables && data.tables[0];
  const filteredColumn = table && table.filteredColumnsConnection && table.filteredColumnsConnection.nodes[0];
  if (table
    && table.unfilteredColumnsConnection
    && filteredColumn
    && filteredColumn.upstreamDatabasesConnection
    && filteredColumn.upstreamTablesConnection
    && filteredColumn.upstreamFlowsConnection
    && filteredColumn.upstreamDatasourcesConnection
    && filteredColumn.downstreamDashboardsConnection
    && filteredColumn.downstreamDatabasesConnection
    && filteredColumn.downstreamTablesConnection
    && filteredColumn.downstreamFlowsConnection
    && filteredColumn.downstreamDatasourcesConnection
    && filteredColumn.downstreamWorkbooksConnection
    && filteredColumn.downstreamSheetsConnection
    && filteredColumn.downstreamMetricsConnection
    && filteredColumn.downstreamOwnersConnection
  ) {
    return {
      filteredColumnName: filteredColumn?.name ?? null,
      columns: table.unfilteredColumnsConnection.totalCount,
      upstreamDatabases: filteredColumn.upstreamDatabasesConnection.totalCount,
      upstreamTables: filteredColumn.upstreamTablesConnection.totalCount,
      upstreamFlows: filteredColumn.upstreamFlowsConnection.totalCount,
      upstreamDatasources: filteredColumn.upstreamDatasourcesConnection.totalCount,
      downstreamDashboards: filteredColumn.downstreamDashboardsConnection.totalCount,
      downstreamDatabases: filteredColumn.downstreamDatabasesConnection.totalCount,
      downstreamTables: filteredColumn.downstreamTablesConnection.totalCount,
      downstreamVirtualConnections: filteredColumn.downstreamVirtualConnectionsConnection?.totalCount || 0,
      downstreamVirtualConnectionTables: filteredColumn.downstreamVirtualConnectionTablesConnection?.totalCount || 0,
      downstreamFlows: filteredColumn.downstreamFlowsConnection.totalCount,
      downstreamDatasources: filteredColumn.downstreamDatasourcesConnection.totalCount,
      downstreamLenses: filteredColumn.downstreamLensesConnection?.totalCount || 0,
      downstreamWorkbooks: filteredColumn.downstreamWorkbooksConnection.totalCount,
      downstreamSheets: filteredColumn.downstreamSheetsConnection.totalCount,
      downstreamMetrics: filteredColumn.downstreamMetricsConnection.totalCount,
      downstreamOwners: filteredColumn.downstreamOwnersConnection.totalCount
    };
  }
  return null;
}

export type TableLineageCountsQueryWrapperProps = {
  readonly tableId: string;
  readonly selectedColumnKeys: KeySet;
} & LineageCountsParentProps<TableLineageCounts>;

export const TableLineageCountsQueryWrapper = (props: TableLineageCountsQueryWrapperProps) => {

  const selectedColumns = Object.keys(props.selectedColumnKeys);
  if (selectedColumns.length > 0) {
    const selectedColumnId = selectedColumns[0];
    return (
      <Query<TableLineageCountsFilteredByColumnQuery, TableLineageCountsFilteredByColumnQueryVariables>
        query={TableLineageCountsFilteredByColumnDocument}
        variables={{ tableId: props.tableId, columnId: selectedColumnId }}
      >
        {result => <>{renderFilteredLineageCounts(result, createUnfilteredCounts, createFilteredCounts, props.children)}</>}
      </Query>
    );
  } else {
    return (
      <Query<TableLineageCountsQuery, TableLineageCountsQueryVariables>
        query={TableLineageCountsDocument}
        variables={{ tableId: props.tableId }}
      >
        {result => <>{renderUnfilteredLineageCounts(result, createUnfilteredCounts, props.children)}</>}
      </Query>
    );
  }
}
